import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { StringEnum } from '../../constants/enums/move-process-enum';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import { formatMessage } from '../../translations/format-message';
import { useStore } from '../../contexts/store-provider';
import { ToolBomUsageModeEnum, ToolBomUsageStateEnum } from '../../models/response/IToolBOMResponse';
import { ProcessTypeEnum } from '../../constants/enums';
import {
  useLedSerial,
  buildStopLedMessage,
  clearLastLedMessage,
} from './hooks/use-led-serial';

export const useOperatorAssignBatch = (context: any) => {
  const { operatorStore, toolBOMStore } = useStore();
  const {
    enabled,
    taskId,
    processType,
    OperatorResponse,
    removeForAbnormalityState,
    GetOperatorService,
    CompleteProcessService,
    completeProcessState,
    resetCompleteProcessState,
    UpdateProcessService,
    startWebWorkerTimer,
    stopWebWorkerTimer,
    resetWebWorkerTimer,
    onSaveTeplarOrder,
    bulidCheckItemsPayload,
    buildMemoPayload,
    buildDimensionPayload,
    buildBOMCheckListPayload,
    mergedTabs,
    trayHistory,
    setTrayHistory,
    // trayIndex,
    setTrayIndex,
    selectedTrayId,
    setSelectedTrayId,
    setActiveIndex,
    setSubmittedCheckItems,
    setMemo,
    setDimensionChecks,
    setCanvasDrawings,
    setBomDataListState,
    startDateTime,
    setStartDateTime,
    startTimer,
    setStartTimer,
    timer,
    setTimer,
    webWorkerTime,
    setWebWorkerTime,
    isTimerRunning,
    setIsTimerRunning,
    isProcessStarted,
    setIsProcessStarted,
    operatorCount,
    setOperatorCount,
    scanBuffer,
    setScanBuffer,
    lastKeyTime,
    setLastKeyTime,
    traySelectRef,
    areAllTabsChecked,
    isBOMValidated,
    bomDataList,
    setShowDraggableStart,
    resetOperatorResponse,
    buildTeplarListPayload,
    getOperatorState,
    resetGetOperatorState,
    isAddTrayInput,
    setIsAddTrayInput,
    teplarList,
    setTeplarList,
    operatorTeplarDetails,
    ledCutList,
    ledCutListState,
    setLedCutListState,
    ledLengthList,
    ledLengthListState,
    setLedLengthListState,
    correctionRequestToggleHandler,
    setOpenNonConformity,
    CheckProcessStatus,
    updateProcessState,
  } = context;

  const { sendCommand, isConnected } = useLedSerial();

  const [batchTrayValidation, setBatchTrayValidation] = useState<
    Record<string, boolean>
  >({});
  const [batchData, setBatchData] = useState<Record<string, any>>({});
  const [, setHasLocalBatchRun] = useState(false);
  const [isTrayRemoved, setIsTrayRemoved] = useState(false);
  // completeProcessState is shared between the Complete and Pause calls (both go through
  // CompleteProcessService) - this ref records which tool numbers to report to
  // UpdateToolStatusByToolManagementNo once completeProcessState.success fires, and stays null
  // for Pause so that call never fires for anything but a real Complete.
  const pendingCompleteToolNumbersRef = useRef<string[] | null>(null);
  // Same idea, but for the Start call (updateProcessState/UpdateProcessService).
  const pendingStartToolNumbersRef = useRef<string[] | null>(null);
  // "trayId::toolNo" pairs whose Start usage row this batch already inserted (B1) - cleared on Complete.
  const startedToolNumbersRef = useRef<string[]>([]);

  useEffect(() => {
    if (!enabled) return;
    if (completeProcessState.success) {
      resetCompleteProcessState();
      if (pendingCompleteToolNumbersRef.current) {
        toolBOMStore.UpdateToolStatusByToolManagementNoService(
          pendingCompleteToolNumbersRef.current,
          false
        );
        pendingCompleteToolNumbersRef.current = null;
      }
    }
  }, [completeProcessState.success, enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (completeProcessState.error) {
      // A failed Complete must not leak into a later Pause's success and wrongly trigger
      // UpdateToolStatusByToolManagementNo for it.
      pendingCompleteToolNumbersRef.current = null;
    }
  }, [completeProcessState.error, enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (updateProcessState.success) {
      if (pendingStartToolNumbersRef.current) {
        toolBOMStore.UpdateToolStatusByToolManagementNoService(
          pendingStartToolNumbersRef.current,
          true
        );
        pendingStartToolNumbersRef.current = null;
      }
    }
  }, [updateProcessState.success, enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (updateProcessState.error) {
      pendingStartToolNumbersRef.current = null;
    }
  }, [updateProcessState.error, enabled]);

  const fetchedBatchTrayIdsRef = useRef<Set<string>>(new Set());
  // Tray whose check items/memo were last hydrated from the server response.
  // Lets the hydration effect tell "new tray" (reset) apart from "same tray,
  // mergedTabs rebuilt by a language switch" (keep operator's input).
  const hydratedTrayIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const currentTrayIds = trayHistory
      .map((trayId: string) => String(trayId || '').trim())
      .filter((trayId: string) => !!trayId);
    fetchedBatchTrayIdsRef.current.forEach((trayId) => {
      if (!currentTrayIds.includes(trayId))
        fetchedBatchTrayIdsRef.current.delete(trayId);
    });
    currentTrayIds.forEach((trayId: string) => {
      if (fetchedBatchTrayIdsRef.current.has(trayId)) return;
      fetchedBatchTrayIdsRef.current.add(trayId);
      toolBOMStore.FetchBatchTrayPriorTools(
        trayId,
        Number(taskId),
        Number(getTenantIdFromLocalStorage())
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, trayHistory]);

  const getResponseMemoForFile = (fileItem: any, fileIndex: number) => {
    const memoKey = fileItem?.TabIndex ?? fileIndex;
    const fileMemoMessages = Array.isArray(fileItem?.MemoMessage)
      ? fileItem.MemoMessage
      : [];
    const responseMemoMessages = Array.isArray(OperatorResponse?.MemoMessage)
      ? OperatorResponse.MemoMessage
      : [];
    const responseTrayId = OperatorResponse?.TrayId ?? selectedTrayId;
    const memoMessages = [...fileMemoMessages, ...responseMemoMessages];

    return (
      memoMessages.find((message: any) => {
        const sameTab =
          message?.TabIndex === undefined ||
          Number(message.TabIndex) === Number(memoKey);
        const sameTray =
          message?.TrayId === undefined ||
          responseTrayId === undefined ||
          String(message.TrayId) === String(responseTrayId);

        return sameTab && sameTray;
      }) ?? null
    );
  };

  const buildTeplarOrderPayload = (list: any[] = []) =>
    list.map((item, index) => ({
      Id: item.Id,
      Order: index + 1,
    }));

  const buildBatchTeplarOrderPayload = () =>
    trayHistory.flatMap((tid) => {
      const source =
        tid === selectedTrayId
          ? teplarList
          : batchData[tid]?.ui.teplarList ||
            batchData[tid]?.payload.teplarList ||
            [];

      return buildTeplarOrderPayload(source);
    });

  const saveTeplarOrderIfNeeded = async () => {
    if (Number(processType) !== 2 || !onSaveTeplarOrder) {
      return;
    }

    await onSaveTeplarOrder(buildBatchTeplarOrderPayload());
  };

  useEffect(() => {
    if (!enabled) return;
    setHasLocalBatchRun(false);
    setTimer(0);
    setWebWorkerTime(0);
    setStartTimer(0);
    setIsTimerRunning(false);
  }, [enabled, setIsTimerRunning, setStartTimer, setTimer, setWebWorkerTime]);

  // Reset state only when all trays have been cleared and a tray was previously selected.
  // Checking `!selectedTrayId` prevents resetting state during initial barcode scan transitions.
  useEffect(() => {
    if (!enabled) return;
    if (trayHistory.length > 0 || !selectedTrayId) return;

    setSelectedTrayId(null);
    setTrayIndex(0);
    setActiveIndex(-1);
    setSubmittedCheckItems({});
    setMemo({});
    setDimensionChecks({});
    setCanvasDrawings({});
    setBomDataListState([]);
    setLedCutListState([]);
    setLedLengthListState([]);
    resetOperatorResponse();
    setTeplarList([]);
  }, [enabled, trayHistory.length, selectedTrayId]);

  // Synchronize selectedTrayId whenever trayHistory updates to ensure the latest scanned tray
  // is selected in the dropdown and its details are loaded if no valid tray is currently active.
  useEffect(() => {
    if (!enabled) return;
    if (
      trayHistory.length > 0 &&
      (!selectedTrayId || !trayHistory.includes(selectedTrayId))
    ) {
      const nextTray = trayHistory[trayHistory.length - 1];
      setSelectedTrayId(nextTray);
      if (
        !OperatorResponse?.TrayId ||
        String(OperatorResponse.TrayId) !== String(nextTray)
      ) {
        getSelectedTrayDetails(nextTray);
      }
    }
  }, [enabled, trayHistory, selectedTrayId, OperatorResponse?.TrayId]);

  useEffect(() => {
    if (!enabled) return;
    if (operatorStore.getOperatorState.success) {
      operatorStore.resetGetOperatorState();
    }
    setIsAddTrayInput(false);
    setIsTrayRemoved(false);
  }, [enabled, operatorStore.getOperatorState.success]);

  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (e) => {
      if (toolBOMStore.isToolScanPopupOpen) return;

      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTime;

      if (!e.key) return;

      if (
        e.key === 'Shift' ||
        e.key === 'Control' ||
        e.key === 'Alt' ||
        e.key === 'CapsLock' ||
        (e.key.length > 1 && e.key !== 'Enter')
      ) {
        return;
      }

      if (e.key === 'Enter') {
        if (timeDiff > 100) {
          e.preventDefault();
          return;
        }
        // if (isTimerRunning) {
        //   e.preventDefault();
        //   toast.error(
        //     formatMessage('please_complete_the_tray_to_scan')
        //   );
        //   return;
        // }
        e.preventDefault();
        const cleanedScan = scanBuffer.trim();
        if (cleanedScan === selectedTrayId) {
          return;
        }
        if (trayHistory.includes(cleanedScan)) {
          // Explicitly select scanned tray when already in history
          setSelectedTrayId(cleanedScan);
          getSelectedTrayDetails(cleanedScan);
          traySelectRef.current?.focus();
          return;
        } else if (isProcessStarted) {
          e.preventDefault();
          toast.error(formatMessage('please_complete_the_tray_to_scan'));
          traySelectRef.current?.focus();
          return;
        } else {
          setTrayHistory((prev) => [...prev, cleanedScan]);
          // Explicitly select newly scanned tray so dropdown updates immediately
          setSelectedTrayId(cleanedScan);
          getSelectedTrayDetails(cleanedScan);
          setScanBuffer('');
          traySelectRef.current?.focus();
          return;
        }
      }

      if (timeDiff > 100) {
        setScanBuffer(e.key);
      } else {
        setScanBuffer((prev) => prev + e.key);
      }

      setLastKeyTime(currentTime);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    enabled,
    isProcessStarted,
    scanBuffer,
    lastKeyTime,
    trayHistory,
    isTimerRunning,
  ]);

  useEffect(() => {
    if (!enabled) return;
    if (operatorStore.getOperatorState.error) {
      operatorStore.resetGetOperatorState();
      const updatedTrayHistory = trayHistory.filter(
        (trayId: string) => trayId !== selectedTrayId
      );

      setTrayHistory(updatedTrayHistory);
      const previousTrayId = updatedTrayHistory[updatedTrayHistory.length - 1];
      if (previousTrayId) {
        setSelectedTrayId(previousTrayId);
        getSelectedTrayDetails(previousTrayId);
      } else {
        setSelectedTrayId(null);
        resetOperatorResponse();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, operatorStore.getOperatorState.error]);

  useEffect(() => {
    if (!enabled) return;
    const hasCurrentTrayResponse =
      OperatorResponse?.TrayId &&
      selectedTrayId &&
      String(OperatorResponse.TrayId) === String(selectedTrayId);

    if (!hasCurrentTrayResponse) {
      if (trayHistory.length === 0) {
        setTimer(0);
      }
      return;
    }

    if (
      OperatorResponse?.IsNC === true ||
      (OperatorResponse?.NCStatus != null && OperatorResponse.NCStatus > -1)
    ) {
      toast.error(
        'Non-conformity trays cannot be processed in batch mode. Please switch to single mode.'
      );
      const updatedTrayHistory = trayHistory.filter(
        (trayId: string) => trayId !== selectedTrayId
      );
      setTrayHistory(updatedTrayHistory);
      const previousTrayId = updatedTrayHistory[updatedTrayHistory.length - 1];
      if (previousTrayId) {
        setSelectedTrayId(previousTrayId);
        getSelectedTrayDetails(previousTrayId);
      } else {
        setSelectedTrayId(null);
        resetOperatorResponse();
      }
      return;
    }

    if (OperatorResponse.CurrentState === StringEnum.Lock) {
      toast.error(formatMessage('partially_processed_error'));
      // setTrayHistory((prev) => prev.slice(0, -1));
      resetOperatorResponse();
    }

    if (OperatorResponse.FileList && OperatorResponse.FileList.length > 0) {
      const isTrue = (val: any) =>
        val === true || String(val).toLowerCase() === 'true';

      let bestIndex = 0;
      let bestPriority = 2;

      OperatorResponse.FileList.forEach((file, index) => {
        let priority = 2;
        if (isTrue(file.IsDefault) || isTrue(file.isDefault)) {
          priority = 0;
        } else if (isTrue(file.IsDimension) || isTrue(file.isDimension)) {
          priority = 1;
        }

        if (priority < bestPriority) {
          bestPriority = priority;
          bestIndex = index;
        }
      });

      setActiveIndex(bestIndex);
    }
  }, [enabled, OperatorResponse, selectedTrayId, trayHistory.length]);

  useEffect(() => {
    if (!enabled) return;
    if (!OperatorResponse?.FileList?.length || !OperatorResponse?.TrayId) {
      // Tray cleared: forget it so a rescan of the same tray starts fresh.
      hydratedTrayIdRef.current = null;
      return;
    }

    if (batchData[OperatorResponse.TrayId]) {
      const hydratedMemo: Record<number, string> = {};

      OperatorResponse.FileList?.forEach((fileItem: any, fileIndex: number) => {
        const memoKey = fileItem?.TabIndex ?? fileIndex;
        const existingMemo = getResponseMemoForFile(fileItem, fileIndex);

        hydratedMemo[memoKey] = existingMemo?.Memo ?? '';
      });

      setSubmittedCheckItems(batchData[OperatorResponse.TrayId].ui.checkItems);
      setMemo({
        ...hydratedMemo,
        ...batchData[OperatorResponse.TrayId].ui.memo,
      });
      setCanvasDrawings(
        batchData[OperatorResponse.TrayId].ui.canvasDrawings || {}
      );
      setBomDataListState(
        batchData[OperatorResponse.TrayId].ui.bomDataListState || []
      );
      return;
    }

    if (!OperatorResponse?.FileList?.length) return;

    const isTrue = (val: any) =>
      val === true || String(val).toLowerCase() === 'true';
    const defaultIndex = mergedTabs.findIndex(
      (t) => isTrue(t.data?.IsDefault) || isTrue(t.data?.isDefault)
    );
    if (defaultIndex !== -1) {
      setActiveIndex(defaultIndex);
    } else {
      const dimensionIndex = mergedTabs.findIndex(
        (t) => isTrue(t.data?.IsDimension) || isTrue(t.data?.isDimension)
      );
      if (dimensionIndex !== -1) {
        setActiveIndex(dimensionIndex);
      } else {
        setActiveIndex(0);
      }
    }

    const hydratedCheckItems: any = {};
    const hydratedMemo: Record<number, string> = {};

    OperatorResponse.FileList?.forEach((fileItem: any, fileIndex: number) => {
      const checkItemsGroups = fileItem?.CheckItems ?? [];
      const memoKey = fileItem?.TabIndex ?? fileIndex;
      const existingMemo = getResponseMemoForFile(fileItem, fileIndex);

      hydratedMemo[memoKey] = existingMemo?.Memo ?? '';

      if (checkItemsGroups.length === 0) return;

      hydratedCheckItems[fileIndex] = {};

      checkItemsGroups.forEach((group: any) => {
        const messages = group?.CheckItemMessage ?? [];
        messages.forEach((item: any) => {
          if (item.Id === undefined || item.Id === null) return;
          hydratedCheckItems[fileIndex][item.Id] = {
            isChecked:
              item.IsChecked === true ||
              String(item.IsChecked).toLowerCase() === 'true',
            InputMessage: item.InputMessage || '',
          };
        });
      });
    });

    // Re-running for the same tray (e.g. mergedTabs rebuilt by a language
    // switch) must not wipe what the operator already entered: use the
    // freshly hydrated values only as defaults and let existing state win.
    const trayId = String(OperatorResponse.TrayId);
    if (hydratedTrayIdRef.current === trayId) {
      setSubmittedCheckItems((prev) => {
        const next: any = { ...hydratedCheckItems };
        Object.keys(prev || {}).forEach((tabKey) => {
          next[tabKey] = { ...(next[tabKey] || {}), ...prev[tabKey] };
        });
        return next;
      });
      setMemo((prev) => ({ ...hydratedMemo, ...prev }));
      return;
    }
    hydratedTrayIdRef.current = trayId;

    setSubmittedCheckItems(hydratedCheckItems);
    setMemo(hydratedMemo);
    setCanvasDrawings({});
  }, [enabled, OperatorResponse, mergedTabs, batchData]);

  useEffect(() => {
    if (!enabled) return;
    if (selectedTrayId && batchData[selectedTrayId]) {
      return;
    }
    if (mergedTabs.length === 0) return;

    const initialState: any = {};

    mergedTabs.forEach((tab, tabIndex) => {
      const checkItems = tab?.data?.CheckItems ?? [];

      checkItems.forEach((item: any) => {
        item.CheckItemMessage?.forEach((msg: any) => {
          if (!initialState[tabIndex]) initialState[tabIndex] = {};
          initialState[tabIndex][msg.Id] = {
            isChecked: msg.IsChecked,
            InputMessage: msg.InputMessage || '',
          };
        });
      });
    });

    // Keep what the operator already entered (e.g. across a language switch,
    // which rebuilds mergedTabs); only fill in entries that don't exist yet.
    setSubmittedCheckItems((prev) => {
      const next: any = { ...prev };
      Object.keys(initialState).forEach((tabKey) => {
        next[tabKey] = { ...initialState[tabKey], ...(prev?.[tabKey] || {}) };
      });
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });
  }, [enabled, mergedTabs, selectedTrayId, batchData]);

  useEffect(() => {
    if (!enabled) return;
    if (!OperatorResponse?.FileList?.length) return;

    if (batchData[OperatorResponse.TrayId]) {
      setDimensionChecks(batchData[OperatorResponse.TrayId].ui.dimensionChecks);
      return;
    }

    const hydratedDimensions: Record<string, boolean> = {};

    OperatorResponse.FileList.forEach((file: any) => {
      const dims = file.DimensionsData ?? [];

      dims.forEach((dim: any) => {
        if (dim.DimensionType !== 1) return;
        const values = dim.DimensionValues ?? [];
        values.forEach((val: string, rowIndex: number) => {
          const [, checkedFlag] = String(val).split('$');
          const key = `${rowIndex}-${dim.Id}`;
          hydratedDimensions[key] = checkedFlag === 'true';
        });
      });
    });

    setDimensionChecks(hydratedDimensions);
  }, [enabled, OperatorResponse, batchData]);

  useEffect(() => {
    if (!enabled || !selectedTrayId) return;
    if (
      OperatorResponse.TrayId &&
      String(OperatorResponse.TrayId) === String(selectedTrayId)
    ) {
      setBatchTrayValidation((prev) => ({
        ...prev,
        [selectedTrayId]: areAllTabsChecked && isBOMValidated,
      }));
    }
  }, [
    enabled,
    areAllTabsChecked,
    OperatorResponse.TrayId,
    isBOMValidated,
    selectedTrayId,
  ]);

  useEffect(() => {
    if (!enabled || !selectedTrayId) return;
    if (
      OperatorResponse?.TrayId &&
      String(OperatorResponse.TrayId) !== String(selectedTrayId)
    ) {
      return;
    }

    if (batchData[selectedTrayId]?.ui.bomDataListState) {
      setBomDataListState(batchData[selectedTrayId].ui.bomDataListState);
    } else if (bomDataList?.length > 0) {
      setBomDataListState(bomDataList);
    } else {
      setBomDataListState([]);
    }
  }, [enabled, selectedTrayId, OperatorResponse?.TrayId, bomDataList]);

  // Only restores a previously-visited tray's saved teplar order/print state.
  // Deriving teplarList from operatorTeplarDetails is owned solely by
  // TeplarPrintTable to avoid two effects racing to set the same state.
  useEffect(() => {
    if (!enabled) return;
    if (selectedTrayId && batchData[selectedTrayId]?.ui.ledCutListState) {
      setLedCutListState(batchData[selectedTrayId].ui.ledCutListState);
    } else if (ledCutList?.length > 0) {
      setLedCutListState(ledCutList);
    } else {
      setLedCutListState([]);
    }
  }, [enabled, ledCutList, selectedTrayId, batchData]);

  useEffect(() => {
    if (!enabled) return;
    if (selectedTrayId && batchData[selectedTrayId]?.ui.ledLengthListState) {
      setLedLengthListState(batchData[selectedTrayId].ui.ledLengthListState);
    } else if (ledLengthList?.length > 0) {
      setLedLengthListState(ledLengthList);
    } else {
      setLedLengthListState([]);
    }
  }, [enabled, ledLengthList, selectedTrayId, batchData]);

  useEffect(() => {
    if (!enabled) return;
    if (selectedTrayId && batchData[selectedTrayId]?.ui.teplarList) {
      setTeplarList(batchData[selectedTrayId].ui.teplarList);
    }
  }, [enabled, selectedTrayId, batchData]);

  // Values sent for a previous tray must not be re-sent when stopping on another tray.
  useEffect(() => {
    clearLastLedMessage();
  }, [selectedTrayId]);

  const stopLEDGlow = async () => {
    if (
      processType !== ProcessTypeEnum.LedCut &&
      processType !== ProcessTypeEnum.LedLength
    ) {
      return;
    }
    // Port already closed: nothing to stop, and do not reconnect.
    if (!isConnected()) return;
    try {
      // Time 0 with the previously sent LED values; zeroed values if none were sent.
      let message = buildStopLedMessage();
      if (!message) {
        message =
          processType === ProcessTypeEnum.LedLength
            ? `1000,0,G,0,R,0,G,0\r\n`
            : `1000,0,R,0\r\n`;
      }

      // Final command: sends stop if the port is open, closes it, never reconnects.
      const response = await sendCommand(message, { final: true });
      console.log('LED Cut device response:', response);

      toast.success(formatMessage('led_command_success'));
    } catch (error: any) {
      console.error('Serial error:', error);
      toast.error(error?.message || formatMessage('led_error_unknown'));
    }
  };

  const saveCurrentTrayData = (trayId: string) => {
    setBatchData((prev) => ({
      ...prev,
      [trayId]: {
        ui: {
          checkItems: context.submittedCheckItems,
          dimensionChecks: context.dimensionChecks,
          memo: context.memo,
          canvasDrawings: context.canvasDrawings,
          bomDataListState: context.bomDataListState,
          teplarList: context.teplarList,
        },
        payload: {
          checkItems: bulidCheckItemsPayload(),
          dimensions: buildDimensionPayload(),
          memo: buildMemoPayload(),
          bom: buildBOMCheckListPayload(),
          teplarList: buildTeplarListPayload(),
        },
      },
    }));
  };

  const removeTrayDataOnProcessCompletion = () => {
    localStorage.removeItem('trayData');
  };

  const buildSavedBOMPayload = (rows: any[] = []) => {
    const isTrue = (value: any) =>
      value === true || value === 1 || String(value).toLowerCase() === 'true';

    return rows.map((item) => ({
      Id: item.Id,
      Pick: isTrue(item.Pick),
      Shortage: isTrue(item.Shortage),
    }));
  };

  const getActiveTrayListServiceHandler = (trayId: string) => {
    // const currentTray = trayHistory[trayIndex];
    if (
      selectedTrayId &&
      selectedTrayId !== trayId &&
      trayHistory.includes(selectedTrayId)
    ) {
      saveCurrentTrayData(selectedTrayId);
    }
    setSelectedTrayId(trayId);
    setActiveIndex(-1);
    resetOperatorResponse();
    setSubmittedCheckItems({});
    setMemo({});
    setDimensionChecks({});
    setCanvasDrawings({});
    setTrayIndex((prevIndex) => (prevIndex + 1) % trayHistory.length);
    GetOperatorService(
      Number(getTenantIdFromLocalStorage()),
      trayId,
      taskId,
      Number(processType)
    );
  };

  const getSelectedTrayDetails = (trayId: string) => {
    if (
      selectedTrayId &&
      selectedTrayId !== trayId &&
      trayHistory.includes(selectedTrayId)
    ) {
      saveCurrentTrayData(selectedTrayId);
    }
    setSelectedTrayId(trayId);
    setActiveIndex(-1);
    resetOperatorResponse();
    setSubmittedCheckItems({});
    setMemo({});
    setDimensionChecks({});
    setCanvasDrawings({});
    setBomDataListState([]);
    GetOperatorService(
      Number(getTenantIdFromLocalStorage()),
      trayId,
      taskId,
      Number(processType)
    );
  };

  const handleRemoveTray = () => {
    const removedTrayId = selectedTrayId;

    const finishRemoval = () => {
      if (!isProcessStarted) {
        const updatedTrayHistory = trayHistory.filter(
          (trayId: string) => trayId !== selectedTrayId
        );

        setTrayHistory(updatedTrayHistory);
        setIsTrayRemoved(true);

        // Last tray was removed
        if (updatedTrayHistory.length === 0) {
          setSelectedTrayId(null);
        }

        if (removedTrayId) {
          setBatchData((prev) => {
            const next = { ...prev };
            delete next[removedTrayId];
            return next;
          });

          setBatchTrayValidation((prev) => {
            const next = { ...prev };
            delete next[removedTrayId];
            return next;
          });
        }
      } else {
        setTrayHistory([]);
        setSelectedTrayId(null);
        setBatchData({});
        setBatchTrayValidation({});
      }

      setHasLocalBatchRun(false);
      setIsProcessStarted(false);
      setIsTimerRunning(false);
      setTimer(0);
      resetWebWorkerTimer();
      setShowDraggableStart?.(true);
      resetOperatorResponse();
    };

    if (!isProcessStarted) {
      const removedTrayIdStr = String(removedTrayId || '').trim();
      const removedTrayTools = toolBOMStore.batchTrayPriorTools[removedTrayIdStr] ?? [];
      toolBOMStore
        .ReleaseToolService(removedTrayTools, [removedTrayIdStr], Number(taskId), ToolBomUsageModeEnum.Batch)
        .then(() => {
          if (!toolBOMStore.releaseToolState.error) finishRemoval();
        });
    } else {
      const allBatchTools = Array.from(
        new Set([
          ...toolBOMStore.scannedToolNumbers,
          ...trayHistory.flatMap(
            (trayId) => toolBOMStore.batchTrayPriorTools[String(trayId || '').trim()] ?? []
          ),
        ])
      );
      toolBOMStore
        .ReleaseToolService(allBatchTools, trayHistory, Number(taskId), ToolBomUsageModeEnum.Batch)
        .then(() => {
          if (!toolBOMStore.releaseToolState.error) finishRemoval();
        });
    }
  };

  const handleNonConformitySuccess = (reportedTrayId?: string | number) => {
    const reportedIdStr = String(reportedTrayId || selectedTrayId || '').trim();
    if (reportedIdStr) {
      setBatchData((prev: any) => {
        const next = { ...prev };
        delete next[reportedIdStr];
        return next;
      });

      setBatchTrayValidation((prev: any) => {
        const next = { ...prev };
        delete next[reportedIdStr];
        return next;
      });
    }

    const currentTrayIdStr = String(OperatorResponse?.TrayId || '').trim();
    const updatedTrayHistory = trayHistory.filter(
      (tray: string) =>
        tray.trim() !== reportedIdStr &&
        tray.trim() !== currentTrayIdStr
    );

    setTrayHistory(updatedTrayHistory);

    if (updatedTrayHistory.length > 0) {
      const nextTray = updatedTrayHistory[updatedTrayHistory.length - 1];
      setSelectedTrayId(nextTray);
      setActiveIndex(-1);
      resetOperatorResponse();
      setSubmittedCheckItems({});
      setMemo({});
      setDimensionChecks({});
      setCanvasDrawings({});
      setBomDataListState([]);
      GetOperatorService(
        Number(getTenantIdFromLocalStorage()),
        nextTray,
        taskId,
        Number(processType)
      );
    } else {
      setSelectedTrayId(null);
      resetOperatorResponse();
      setShowDraggableStart?.(true);
    }
  };

  const buildBatchPayload = (isPaused: boolean) => ({
    TenantId: Number(getTenantIdFromLocalStorage()),
    TrayId: trayHistory.map((trayId) => String(trayId || '').trim()),
    Count: trayHistory.length,
    ProcessId: taskId,
    StartTime: startDateTime,
    EndTime: new Date().toISOString(),
    Duration: webWorkerTime - startTimer,
    NoOfOperators: operatorCount,
    CurrentState: '',
    IsPaused: isPaused,
    IsBatch: true,
    CheckItemList: trayHistory.flatMap(
      (tid) =>
        (tid === selectedTrayId
          ? bulidCheckItemsPayload()
          : batchData[tid]?.payload.checkItems) || []
    ),
    MemoMessage: trayHistory.flatMap(
      (tid) =>
        (tid === selectedTrayId
          ? buildMemoPayload()
          : batchData[tid]?.payload.memo) || []
    ),
    Dimensions: trayHistory.flatMap(
      (tid) =>
        (tid === selectedTrayId
          ? buildDimensionPayload()
          : batchData[tid]?.payload.dimensions) || []
    ),
    BOMCheckList: trayHistory.flatMap((tid) => {
      if (tid === selectedTrayId) {
        return buildBOMCheckListPayload();
      }

      const savedPayload = batchData[tid]?.payload.bom;
      if (savedPayload && savedPayload.length > 0) {
        return savedPayload;
      }

      return buildSavedBOMPayload(batchData[tid]?.ui.bomDataListState);
    }),
    TeplarList: trayHistory.flatMap(
      (tid) =>
        (tid === selectedTrayId
          ? buildTeplarListPayload()
          : batchData[tid]?.payload.teplarList) || []
    ),
    IsFollowUp: processType === ProcessTypeEnum.FollowUpPicking,
  });

  const completeSuccess = () => {
    startedToolNumbersRef.current = [];
    setHasLocalBatchRun(false);
    setIsTimerRunning(false);
    setTimer(0);
    resetWebWorkerTimer();
    setOperatorCount(1);
    setIsProcessStarted(false);
    setTrayHistory([]);
    setSelectedTrayId(null);
    // Batch finished - clear the scanned tools so the next batch starts with an empty list.
    toolBOMStore.resetSessionToolTracking();
    // Also clear the fetched Target Tool List (Get button result) so the next batch starts fresh.
    toolBOMStore.resetToolBomToolManagementList();
    setShowDraggableStart?.(true);
    setMemo([]);
    setDimensionChecks([]);
    setSubmittedCheckItems({});
    setCanvasDrawings({});
    setBatchTrayValidation({});
    setBatchData({});
    setBomDataListState([]);
    setActiveIndex(-1);
    resetOperatorResponse();
    removeTrayDataOnProcessCompletion();
    resetWebWorkerTimer();
  };

  const handleCompleteProcess = async () => {
    saveTeplarOrderIfNeeded();
    stopLEDGlow();
    // Only Complete (not Pause) should mark the used tools Inspected/InUse once the Operator
    // request succeeds.
    // Includes each tray's earlier tools too (fetched when it was scanned), so they get their final
    // status along with the tools scanned for this batch.
    pendingCompleteToolNumbersRef.current = Array.from(
      new Set([
        ...toolBOMStore.scannedToolNumbers,
        ...trayHistory.flatMap(
          (trayId) =>
            toolBOMStore.batchTrayPriorTools[String(trayId || '').trim()] ?? []
        ),
      ])
    );
    // Complete now reports Tool BOM usage inline in this same Operator request instead of a
    // separate ToolManagement/ToolBomUsageInfo call (Start still makes that call on its own).
    const submitObj = {
      ...buildBatchPayload(false),
      ToolBomUsageInfoDetailss: toolBOMStore.BuildBatchToolBomUsageDetails(
        trayHistory,
        Number(taskId),
        webWorkerTime - startTimer,
        ToolBomUsageStateEnum.Complete
      ),
    };
    const completed = await CompleteProcessService(submitObj);
    if (!completed) return;
    completeSuccess();
  };

  const handleStartClick = async () => {
    if (processType !== ProcessTypeEnum.FollowUpPicking) {
      const updateObj = {
        TenantId: Number(getTenantIdFromLocalStorage()),
        TrayId: trayHistory?.map((tray) => tray.trim()),
        ProcessId: taskId,
        SetActive: true,
      };
      await CheckProcessStatus(updateObj);
    } else {
      handleStartTimer();
    }
  };

  const handleStartTimer = async () => {
    const startTrayIds = trayHistory.map((trayId) =>
      String(trayId || '').trim()
    );
    pendingStartToolNumbersRef.current = [...toolBOMStore.scannedToolNumbers];
    const updateObj = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      TrayId: startTrayIds,
      ProcessId: taskId,
      SetActive: true,
      // Same shape sent on Pause/Complete, but Duration is always 0 here since the process is
      // just starting.
      ToolBomUsageInfoDetailss: toolBOMStore.BuildBatchToolBomUsageDetails(
        startTrayIds,
        Number(taskId),
        0,
        ToolBomUsageStateEnum.Start,
        [],
        startedToolNumbersRef.current
      ),
    };
    if (processType !== ProcessTypeEnum.FollowUpPicking) {
      const started = await UpdateProcessService(updateObj);
      if (!started) return;
    }
    // Remember which tray+tool Start rows now exist, so a Pause -> Start resume doesn't insert them again.
    startedToolNumbersRef.current = Array.from(
      new Set([
        ...startedToolNumbersRef.current,
        ...startTrayIds.flatMap((trayId) =>
          toolBOMStore.scannedToolNumbers.map(
            (tool: string) => `${trayId}::${tool}`
          )
        ),
      ])
    );
    setHasLocalBatchRun(true);
    startWebWorkerTimer();
    setStartDateTime(new Date().toISOString());
    setIsTimerRunning(true);
    setIsProcessStarted(true);
    setStartTimer(webWorkerTime);
  };

  const PausedSuccess = () => {
    setHasLocalBatchRun(true);
    setIsTimerRunning(false);
    setScanBuffer('');
    setLastKeyTime(Date.now());
  };

  const handlePauseTimer = () => {
    stopWebWorkerTimer();
    saveTeplarOrderIfNeeded();
    stopLEDGlow();
    // Pause now reports Tool BOM usage inline in this same Operator request instead of a
    // separate ToolManagement/ToolBomUsageInfo call (Start still makes that call on its own).
    const submitObj = {
      ...buildBatchPayload(true),
      ToolBomUsageInfoDetailss: toolBOMStore.BuildBatchToolBomUsageDetails(
        trayHistory,
        Number(taskId),
        webWorkerTime - startTimer,
        ToolBomUsageStateEnum.Pause
      ),
    };
    const pausePromise = CompleteProcessService(submitObj);
    PausedSuccess();
    return pausePromise;
  };

  const removeTrayServiceHandler = () => {
    const obj = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      TrayId: trayHistory?.map((tray) => tray.trim()),
      ProcessId: taskId,
      SetActive: false,
    };
    handleRemoveTray();
  };

  const handleTraySelectChange = (nextTrayId: string) => {
    getSelectedTrayDetails(nextTrayId);
  };

  const addTray = (trayId: string) => {
    if (isTimerRunning) return;
    if (trayHistory.includes(trayId)) {
      // toast.error(formatMessage('tray_id_already_added'));
      setSelectedTrayId(trayId);
      getSelectedTrayDetails(trayId);
      return;
    } else {
      setTrayHistory((prev) => [...prev, trayId]);
      // Select the newly added tray so dropdown reflects the selection
      setSelectedTrayId(trayId);
      getActiveTrayListServiceHandler(trayId);
    }
  };

  const getTrayValidation = (tray: string) => {
    if (tray === selectedTrayId) {
      return areAllTabsChecked && isBOMValidated;
    }

    return batchTrayValidation[tray] === true;
  };

  const shouldShowTrayWarning = (tray: string) => {
    if (!isTimerRunning) return false;
    const result = !getTrayValidation(tray);
    return result;
  };

  const shouldShowCheckMark = (tray: string) => {
    if (!isTimerRunning) return false;
    const result = getTrayValidation(tray);
    return result;
  };

  const handleCorrectionRequest = () => {
    correctionRequestToggleHandler();
  };

  const nonConformityHandler = () => {
    setOpenNonConformity(true);
    if (isTimerRunning) {
      handlePauseTimer();
    }
  };

  const buttons = [
    ...(isTimerRunning
      ? [
          {
            labelKey: 'pause',
            className: 'btn-secondary',
            onClick: handlePauseTimer,
            isDisabled: !isTimerRunning,
          },
          {
            labelKey: 'complete',
            className: 'btn-primary',
            onClick: handleCompleteProcess,
            isDisabled:
              trayHistory.length === 0 ||
              !isProcessStarted ||
              !trayHistory.every((trayId) => getTrayValidation(trayId)),
          },
        ]
      : trayHistory.length > 0
        ? [
            {
              labelKey: 'start',
              className: 'btn-success',
              onClick: handleStartClick,
              isDisabled: trayHistory.length === 0 || isTimerRunning,
            },
          ]
        : []),
    {
      labelKey: 'remove',
      className: 'btn-danger',
      onClick: removeTrayServiceHandler,
      isDisabled: trayHistory.length === 0 || isTimerRunning,
    },
    {
      labelKey: 'correction_request',
      className: 'btn-outline-danger',
      onClick: handleCorrectionRequest,
      isDisabled: trayHistory.length === 0,
    },
    {
      labelKey: 'non_conformity',
      className: 'btn-outline-danger',
      onClick: nonConformityHandler,
      isDisabled: trayHistory.length === 0,
    },
  ];

  return {
    buttons,
    shouldShowTrayWarning,
    handleTraySelectChange,
    addTray,
    handlePauseTimer,
    shouldShowCheckMark,
    handleStartTimer,
    handleRemoveTray,
    handleNonConformitySuccess,
    handleStartClick,
    completeSuccess,
    PausedSuccess,
  };
};
